import { useToast } from '../components/ui/Toast';
import { useCallback } from 'react';

export interface WebSocketMessage {
  type: string;
  data?: any;
  timestamp?: string;
}

export interface InviteNotification {
  type: 'invite';
  inviteId: string;
  groupId: string;
  inviterDisplayName: string;
  inviterPublicHandle: string;
  message: string;
}

export interface InviteAcceptedCallback {
  (data: any): void;
}

class WebSocketNotificationService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private pingInterval: NodeJS.Timeout | null = null;
  private isConnected = false;
  private currentUserId: string | null = null;
  private websocketEndpoint: string;
  private addToast: ((toast: { type: 'success' | 'error' | 'warning' | 'info'; title: string; message?: string; action?: { label: string; onClick: () => void; }; duration?: number; }) => void) | null = null;
  private onInviteAcceptedCallback: InviteAcceptedCallback | null = null;

  constructor() {
    // You'll need to set this from your CDK output or environment variable
    this.websocketEndpoint = process.env.NEXT_PUBLIC_WEBSOCKET_ENDPOINT || '';
  }

  setToastHandler(addToast: (toast: { type: 'success' | 'error' | 'warning' | 'info'; title: string; message?: string; action?: { label: string; onClick: () => void; }; duration?: number; }) => void) {
    this.addToast = addToast;
  }

  setInviteAcceptedCallback(callback: InviteAcceptedCallback) {
    this.onInviteAcceptedCallback = callback;
  }

  connect(userId: string) {
    if (!this.websocketEndpoint) {
      console.error('WebSocket endpoint not configured');
      return;
    }

    if (this.ws && this.isConnected) {
      return; // Already connected
    }

    this.currentUserId = userId;

    try {
      const wsUrl = `${this.websocketEndpoint}?userId=${userId}`;
      console.log('Connecting to WebSocket with userId:', userId);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // Send connection confirmation after ensuring WebSocket is fully ready
        this.sendConnectionConfirmation(userId);

        this.startPing();
      };
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  private handleMessage(event: MessageEvent) {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);

      switch (message.type) {
        case 'invite-sent':
          this.handleInviteNotification(message.data);
          break;
        case 'invite-accepted':
          this.handleInviteAcceptedNotification(message.data);
          break;
        case 'pong':
           console.log('Received pong from server');
          break;
        case 'error':
          this.handleErrorMessage(message.data);
          break;
        default:
          console.log('Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  private handleInviteNotification(inviteData: InviteNotification) {
    console.log('Received invite notification:', inviteData);

    // Play notification sound
    this.playNotificationSound();

    if (!this.addToast) {
      console.warn('Toast handler not set - cannot show notification');
      console.log('Available toast handler:', !!this.addToast);
      return;
    }

    console.log('Showing toast notification for invite');

    this.addToast({
      type: 'info',
      title: 'Commentary Invitation',
      message: inviteData.message, // Use the message from the payload directly
      duration: 10000, // 10 seconds
      action: {
        label: 'View Invitation',
        onClick: () => {
          window.location.href = `/invites`;
        }
      }
    });
  }

  private handleInviteAcceptedNotification(inviteData: any) {
    console.log('Received invite accepted notification:', inviteData);

    // Play notification sound
    this.playNotificationSound();

    if (!this.addToast) {
      console.warn('Toast handler not set - cannot show notification');
      console.log('Available toast handler:', !!this.addToast);
      return;
    }

    console.log('Showing toast notification for invite accepted');

    this.addToast({
      type: 'success',
      title: 'Co-Commentator Joined',
      message: inviteData.message, // "[Name] has joined the commentary team"
      duration: 8000, // 8 seconds
    });

    // Trigger the external callback if set
    if (this.onInviteAcceptedCallback) {
      this.onInviteAcceptedCallback(inviteData);
    }
  }

  private playNotificationSound() {
    try {
      // Create a simple notification sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Create a short, pleasant notification sound (two-tone chime)
      const playTone = (frequency: number, startTime: number, duration: number) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(frequency, startTime);
        oscillator.type = 'sine';

        // Fade in and out to avoid clicking
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.1, startTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0.1, startTime + duration - 0.01);
        gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = audioContext.currentTime;
      playTone(800, now, 0.2); // First tone
      playTone(600, now + 0.1, 0.2); // Second tone (overlapping)

    } catch (error) {
      console.warn('Could not play notification sound:', error);
      // Fallback to system beep if available
      if ('vibrate' in navigator) {
        navigator.vibrate(200);
      }
    }
  }

  private handleErrorMessage(errorData: any) {
    if (!this.addToast) return;

    this.addToast({
      type: 'error',
      title: 'Notification Error',
      message: errorData.message || 'An error occurred with notifications',
      duration: 5000
    });
  }

  private handleClose(event: CloseEvent) {
    console.log('WebSocket disconnected:', event.code, event.reason);
    this.isConnected = false;
    this.stopPing();

    // Only attempt to reconnect if it wasn't a normal closure
    if (event.code !== 1000 && this.currentUserId) {
      this.scheduleReconnect();
    }
  }

  private handleError(error: Event) {
    console.error('WebSocket error:', error);
    this.isConnected = false;
    this.stopPing();
  }

  private startPing() {
    this.stopPing(); // Clear any existing interval

    this.pingInterval = setInterval(() => {
      if (this.ws && this.isConnected) {
        this.ws.send(JSON.stringify({
          type: 'ping',
          timestamp: new Date().toISOString()
        }));
      }
    }, 30000); // 30 seconds
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      if (this.addToast) {
        this.addToast({
          type: 'error',
          title: 'Connection Lost',
          message: 'Unable to maintain connection to notification service. Please refresh the page.',
          duration: 0 // Persistent notification
        });
      }
      return;
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (this.currentUserId) {
        this.connect(this.currentUserId);
      }
    }, delay);
  }

  disconnect() {
    this.stopPing();

    if (this.ws) {
      this.ws.close(1000, 'User disconnect');
      this.ws = null;
    }

    this.isConnected = false;
    this.currentUserId = null;
    this.reconnectAttempts = 0;
  }

  isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  // Method to manually send messages if needed
  sendMessage(message: WebSocketMessage) {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }

  private sendConnectionConfirmation(userId: string) {
    const attemptSend = (attempt: number = 1) => {
      if (!this.ws || !this.isConnected) {
        console.log('WebSocket no longer available, skipping connection confirmation');
        return;
      }

      // Check if WebSocket is actually ready to send messages
      if (this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({
            type: 'connection_established',
            data: {
              userId: userId,
              timestamp: new Date().toISOString()
            }
          }));
          console.log('Connection confirmation sent successfully');
        } catch (error) {
          console.error('Failed to send connection confirmation:', error);
        }
      } else if (this.ws.readyState === WebSocket.CONNECTING && attempt <= 10) {
        // Still connecting, wait a bit and try again (max 10 attempts)
        setTimeout(() => attemptSend(attempt + 1), 50);
      } else {
        console.warn('WebSocket not ready for sending, skipping connection confirmation');
      }
    };

    attemptSend();
  }
}

// Create a singleton instance
export const webSocketNotificationService = new WebSocketNotificationService();

// Hook for easy integration with React components
export const useWebSocketNotifications = () => {
  const { addToast } = useToast();

  // Memoize the toast handler to prevent unnecessary re-initializations
  const stableAddToast = useCallback(addToast, []);

  const connectNotifications = useCallback((userId: string) => {
    console.log('Setting toast handler for WebSocket notifications');
    webSocketNotificationService.setToastHandler(stableAddToast);
    console.log('Connecting to WebSocket with userId:', userId);
    webSocketNotificationService.connect(userId);
  }, [stableAddToast]);

  const disconnectNotifications = useCallback(() => {
    console.log('Disconnecting WebSocket notifications');
    webSocketNotificationService.disconnect();
  }, []);

  const setInviteAcceptedCallback = useCallback((callback: InviteAcceptedCallback) => {
    webSocketNotificationService.setInviteAcceptedCallback(callback);
  }, []);

  return {
    connect: connectNotifications,
    disconnect: disconnectNotifications,
    setInviteAcceptedCallback,
    isConnected: webSocketNotificationService.isWebSocketConnected.bind(webSocketNotificationService),
    sendMessage: webSocketNotificationService.sendMessage.bind(webSocketNotificationService)
  };
};
